import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  email: string;
  name: string;
  isAdmin?: boolean;
  id?: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signup: (email: string, name: string, password: string) => Promise<{ success: boolean; message: string }>;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  adminLogin: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('collegeSuccessUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('collegeSuccessUser');
      }
    }
    setIsLoading(false);
  }, []);

  // Helper function to hash passwords (simple for demo - use bcrypt in production)
  const hashPassword = (password: string): string => {
    return btoa(password + 'salt_collegesuccess_2024');
  };

  // Get stored users from localStorage
  const getStoredUsers = (): Array<User & { password: string }> => {
    const stored = localStorage.getItem('collegeSuccessUsers');
    return stored ? JSON.parse(stored) : [];
  };

  // Save users to localStorage
  const saveUsers = (users: Array<User & { password: string }>) => {
    localStorage.setItem('collegeSuccessUsers', JSON.stringify(users));
  };

  const signup = async (email: string, name: string, password: string): Promise<{ success: boolean; message: string }> => {
    // Validate input
    if (!email || !name || !password) {
      return { success: false, message: 'All fields are required' };
    }

    if (password.length < 6) {
      return { success: false, message: 'Password must be at least 6 characters' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    // Check if user already exists
    const existingUsers = getStoredUsers();
    if (existingUsers.find(user => user.email.toLowerCase() === email.toLowerCase())) {
      return { success: false, message: 'An account with this email already exists' };
    }

    // Create new user
    const newUser: User & { password: string } = {
      id: Date.now().toString(),
      email: email.toLowerCase(),
      name,
      createdAt: new Date().toISOString(),
      password: hashPassword(password)
    };

    // Save to storage
    const updatedUsers = [...existingUsers, newUser];
    saveUsers(updatedUsers);

    // Set as current user (remove password from user object)
    const { password: _, ...userWithoutPassword } = newUser;
    setUser(userWithoutPassword);
    localStorage.setItem('collegeSuccessUser', JSON.stringify(userWithoutPassword));

    return { success: true, message: 'Account created successfully!' };
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    if (!email || !password) {
      return { success: false, message: 'Email and password are required' };
    }

    const existingUsers = getStoredUsers();
    const user = existingUsers.find(u =>
      u.email.toLowerCase() === email.toLowerCase() &&
      u.password === hashPassword(password)
    );

    if (!user) {
      return { success: false, message: 'Invalid email or password' };
    }

    // Set as current user (remove password from user object)
    const { password: _, ...userWithoutPassword } = user;
    setUser(userWithoutPassword);
    localStorage.setItem('collegeSuccessUser', JSON.stringify(userWithoutPassword));

    return { success: true, message: 'Login successful!' };
  };

  const adminLogin = () => {
    const adminData: User = {
      email: 'admin@collegesuccess.ai',
      name: 'Admin User',
      isAdmin: true,
      id: 'admin',
      createdAt: new Date().toISOString()
    };
    setUser(adminData);
    localStorage.setItem('collegeSuccessUser', JSON.stringify(adminData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('collegeSuccessUser');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signup,
    login,
    adminLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};